/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
/* eslint-disable prefer-destructuring */

import $ from 'jquery';
import cookie from 'react-cookies';
import { isAccessExpired, reloadForAccessExpiry } from 'utils/auth';
import { CSRF_TOKEN_NAME } from 'utils/envVars';
import Constants from '../constants';

const { internalApiHost } = Constants;

const paramStringFromObject = (params) => {
  const searchParams = new URLSearchParams();

  Object.keys(params).map((key) => searchParams.set(key, params[key]));

  return `?${searchParams.toString()}`;
};

const createHeadersAccess = (accessToken) => {
  return {
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
  };
};

const createHeadersRefresh = () => {
  return {
    'X-CSRF-TOKEN': cookie.load(CSRF_TOKEN_NAME),
  };
};

// Revalidate the bearer actually sent, not a later login's token. Only reads
// are replayed; exports and other writes retain their existing one-shot behavior.
const ajaxWithHistoryValidation = (options, authApi) => {
  let replayed = false;
  const send = (settings) =>
    $.ajax({
      ...settings,
      error: async (response) => {
        if (authApi?.isHistorySession && response.status === 401) {
          const authorization = settings.headers?.Authorization;
          const rejectedAccessToken = authorization?.startsWith('Bearer ')
            ? authorization.slice(7)
            : null;
          let validatedToken = null;
          if (rejectedAccessToken && !replayed) {
            try {
              validatedToken = await authApi.refreshAccessToken({
                rejectedAccessToken,
              });
            } catch {
              // A failed validation must still settle the original request.
            }
          }
          if (settings.type === 'GET' && !replayed && validatedToken) {
            replayed = true;
            send({ ...settings, headers: createHeadersAccess(validatedToken) });
            return;
          }
        } else if (isAccessExpired(response.status, response)) {
          reloadForAccessExpiry();
        }
        options.error(response);
      },
    });
  return send(options);
};

class Requests {
  authApi = null;

  setAuthApi(authApi) {
    this.authApi = authApi;
  }

  get(path, params) {
    const authApi = this.authApi;
    const paramString = params ? paramStringFromObject(params) : '';

    const promise = new Promise(function (resolve) {
      ajaxWithHistoryValidation(
        {
          url: `${internalApiHost}${path}${paramString}`,
          type: 'GET',
          headers: createHeadersAccess(authApi?.accessToken),
          timeout: 30000,
          success: (data) => {
            resolve(data);
          },
          error: () => {
            resolve(false);
          },
        },
        authApi,
      );
    });
    return promise;
  }

  post(
    path,
    data,
    refreshHeaders,
    method = 'POST',
    rejectErrors = false,
    withCredentials = false,
    preserveErrorResponse = false,
  ) {
    const authApi = this.authApi;
    const promise = new Promise((resolve, reject) => {
      const headers = refreshHeaders
        ? createHeadersRefresh()
        : createHeadersAccess(authApi?.accessToken);

      ajaxWithHistoryValidation(
        {
          url: `${internalApiHost}${path}`,
          type: method,
          headers,
          xhrFields: withCredentials ? { withCredentials: true } : undefined,
          // async: false,
          contentType: 'application/json; charset=utf-8',
          dataType: 'json',
          data: JSON.stringify(data),
          success(data) {
            resolve(data);
          },
          error: async (result) => {
            if (preserveErrorResponse) {
              return resolve({ ...result.responseJSON, success: false });
            }
            const isBadRequest = result.status === 400;

            if (isBadRequest) {
              if (rejectErrors) {
                const { errors } = result.responseJSON;
                return reject(errors);
              }

              return resolve(false);
            }

            resolve(false);
          },
        },
        authApi,
      );
    });
    return promise;
  }

  patch(path, data, refreshHeaders, rejectErrors = false) {
    return this.post(path, data, refreshHeaders, 'PATCH', rejectErrors);
  }

  put(path, data, refreshHeaders, rejectErrors = false) {
    return this.post(path, data, refreshHeaders, 'PUT', rejectErrors);
  }

  delete(path, data, refreshHeaders, rejectErrors = false) {
    return this.post(path, data, refreshHeaders, 'DELETE', rejectErrors);
  }

  postWithCredentials(path, data, refreshHeaders, rejectErrors = false) {
    return this.post(path, data, refreshHeaders, 'POST', rejectErrors, true);
  }

  postReject(path, data, refreshHeaders) {
    // Totally temporary... remove me when networking is fixed
    return this.post(path, data, refreshHeaders, 'POST', true);
  }

  postWithErrorResponse(path, data) {
    return this.post(path, data, undefined, 'POST', false, false, true);
  }

  postFormData(path, data) {
    const authApi = this.authApi;
    const promise = new Promise((resolve) => {
      const headers = createHeadersAccess(authApi?.accessToken);

      ajaxWithHistoryValidation(
        {
          url: `${internalApiHost}${path}`,
          type: 'POST',
          headers,
          async: false,
          processData: false,
          contentType: false,
          data,
          success(data) {
            resolve(data);
          },
          error: async (result) => {
            console.log('API ERROR', JSON.stringify(result));
            resolve(false);
          },
        },
        authApi,
      );
    });
    return promise;
  }
}

// Export as a singleton, so we can alter it's state to do things like changing which access token is used
export default new Requests();
